<script setup lang="ts">
import ContactAddress from "@/components/ContactAddress.vue"
import VarsityInlineConfirm from "@/components/VarsityInlineConfirm.vue"
import VarsityInlineConfirmButton from "@/components/VarsityInlineConfirmButton.vue"
import VarsityInlineConfirmGroup from "@/components/VarsityInlineConfirmGroup.vue"
import { useLegacyHttpClient } from "@/composables/compat/useLegacyHttpClient"
import { createInlineConfirm } from "@/composables/createInlineConfirm"
import { type ApproveOrganizationDebitorProfileTask } from "@/model/app/approve-organization-debitor-profile-task"
import { toRefs } from "vue"
import { type TasksProps } from "./tasks-props"

const props = defineProps<TasksProps<ApproveOrganizationDebitorProfileTask>>()

const emit = defineEmits<{
    (e: "updated"): void
}>()

const { task } = toRefs(props)

const http = useLegacyHttpClient()

const inlineConfirmApprove = createInlineConfirm({
    async action() {
        await http.post(`/organization/${props.issue.owner.id}/debitor-profile/approve-task/${props.task.id}`, {
            approved: true,
        })
    },
    afterAction() {
        emit("updated")
    },
})

const inlineConfirmDecline = createInlineConfirm({
    async action() {
        await http.post(`/organization/${props.issue.owner.id}/debitor-profile/approve-task/${props.task.id}`, {
            approved: false,
        })
    },
    afterAction() {
        emit("updated")
    },
})
</script>

<template>
    <div class="p-4">
        <dl>
            <dt>Name</dt>
            <dd>
                <h6 class="m-0">{{ task.organizationName }}</h6>
            </dd>

            <dt>{{ $t("@tasks:tasks.approve_organization_debitor_profile.accountNumber") }}</dt>
            <dd>{{ task.accountNumber }}</dd>
        </dl>

        <div class="row">
            <div class="col-md-6">
                <dl>
                    <dt>{{ $t("@tasks:tasks.approve_organization_debitor_profile.old_values") }}</dt>
                    <dd v-if="task.approveType === 'edit'">
                        <ContactAddress
                            :address="{
                                name: task.oldValues?.name,
                                addressLine: task.oldValues?.addressLine,
                                addressLine2: task.oldValues?.addressLine2,
                                street: task.oldValues?.street,
                                streetNumber: task.oldValues?.streetNumber,
                                zip: task.oldValues?.zip,
                                city: task.oldValues?.city,
                                country: task.oldValues?.country,
                            }"
                        ></ContactAddress>
                    </dd>
                    <dd v-else>
                        <div class="py-5 text-center text-muted">
                            {{ $t("@tasks:tasks.approve_organization_debitor_profile.no_old_values") }}
                        </div>
                    </dd>
                </dl>
            </div>
            <div class="col-md-6">
                <dl>
                    <dt>{{ $t("@tasks:tasks.approve_organization_debitor_profile.new_values") }}</dt>
                    <dd>
                        <ContactAddress
                            :address="{
                                name: task.newValues?.name,
                                addressLine: task.newValues?.addressLine,
                                addressLine2: task.newValues?.addressLine2,
                                street: task.newValues?.street,
                                streetNumber: task.newValues?.streetNumber,
                                zip: task.newValues?.zip,
                                city: task.newValues?.city,
                                country: task.newValues?.country,
                            }"
                            :old-address="{
                                name: task.oldValues?.name,
                                addressLine: task.oldValues?.addressLine,
                                addressLine2: task.oldValues?.addressLine2,
                                street: task.oldValues?.street,
                                streetNumber: task.oldValues?.streetNumber,
                                zip: task.oldValues?.zip,
                                city: task.oldValues?.city,
                                country: task.oldValues?.country,
                            }"
                        ></ContactAddress>
                    </dd>
                </dl>
            </div>
        </div>

        <div v-if="props.task.state === 'closed'" class="my-4 text-center">
            {{ $t("@tasks:tasks.approve_organization_debitor_profile.task_approved") }}
        </div>

        <div v-if="props.task.state === 'rejected'" class="my-4 text-center">
            {{ $t("@tasks:tasks.approve_organization_debitor_profile.task_rejected") }}
        </div>

        <div v-if="viewMode === 'admin' && task.state === 'open'" class="mt-4">
            <VarsityInlineConfirmGroup>
                <VarsityInlineConfirmButton class="btn btn-success btn-shape-skewed" :controller="inlineConfirmApprove">
                    {{ $t("@tasks:tasks.approve_organization_debitor_profile.approve") }}
                </VarsityInlineConfirmButton>
                <VarsityInlineConfirmButton class="btn btn-danger btn-shape-skewed" :controller="inlineConfirmDecline">
                    {{ $t("@tasks:tasks.account_invitation.decline") }}
                </VarsityInlineConfirmButton>

                <VarsityInlineConfirm :controller="inlineConfirmApprove">
                    <template #confirmation>
                        {{ $t("@tasks:tasks.approve_organization_debitor_profile.approve_confirm") }}
                    </template>
                    <template #success>
                        {{ $t("@tasks:tasks.approve_organization_debitor_profile.approve_successfull") }}
                    </template>
                </VarsityInlineConfirm>

                <VarsityInlineConfirm :controller="inlineConfirmDecline">
                    <template #confirmation>
                        {{ $t("@tasks:tasks.approve_organization_debitor_profile.decline_confirm") }}
                    </template>
                    <template #success>
                        {{ $t("@tasks:tasks.approve_organization_debitor_profile.decline_successfull") }}
                    </template>
                </VarsityInlineConfirm>
            </VarsityInlineConfirmGroup>
        </div>
    </div>
</template>
